import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import '../stile-avvisi.css'
import { api } from '../servizi/api.ts'
import { ascoltaRiquadri } from '../servizi/avvisi.ts'
import { restaNellaFinestra } from '../servizi/posta-finestra.ts'
import { accoda, TIPO_MESSENGER, type Voce } from '../servizi/regole-avvisi.ts'
import { Etichetta, BottoneIcona } from './base.tsx'
import { IconaBusta, IconaCampana, IconaChiudi, IconaMessaggi } from './Icone.tsx'

/** Dopo quanto un riquadro si chiude da solo. Il conto si ferma con il puntatore o il fuoco sopra. */
const DURATA = 8_000

/**
 * Riquadri degli avvisi in basso a destra (richiesta di Davide del 2/11/2026): al massimo tre impilati,
 * il piu' recente in fondo; sul telefono a tutta larghezza in basso (stile-avvisi.css). Li riempie
 * servizi/avvisi.ts quando arriva qualcosa e la scheda e' visibile.
 *
 * Una email dice casella, mittente e oggetto, e il clic apre la conversazione; una notifica dice il
 * titolo, e il clic porta al suo collegamento o alle notifiche, segnandola letta. Ognuno ha la crocetta
 * e si chiude da solo dopo otto secondi, contati solo mentre la pagina e' visibile e nessuno ci tiene
 * sopra il puntatore o il fuoco. L'area e' `role="status"`: i lettori di schermo annunciano le voci
 * nuove senza spostare il fuoco.
 */
export function AvvisiSchermo() {
  const [voci, setVoci] = useState<Voce[]>([])
  const vai = useNavigate()

  useEffect(() => ascoltaRiquadri((nuove, altre, soloEmail) => {
    setVoci((attuali) => accoda(attuali, nuove, altre, soloEmail))
  }), [])

  const chiudi = useCallback((chiave: string) => setVoci((v) => v.filter((x) => x.chiave !== chiave)), [])
  const apri = (v: Voce) => {
    chiudi(v.chiave)
    // Aperta dal riquadro, la notifica e' letta. E' un gesto della persona: rinnova la sessione.
    if (v.notificaId) void api.post('/notifiche/lette', { ids: [v.notificaId] }).catch(() => undefined)
    // Nella finestra della posta l'email si apre li', senza tornare al gestionale.
    vai(restaNellaFinestra(v.dove, window.location.pathname))
  }

  return (
    <div className="avvisi-schermo" role="status" aria-live="polite" aria-label="Avvisi">
      {voci.map((v) => <RiquadroAvviso key={v.chiave} voce={v} apri={apri} chiudi={chiudi} />)}
    </div>
  )
}

function RiquadroAvviso({ voce, apri, chiudi }: {
  voce: Voce; apri: (v: Voce) => void; chiudi: (chiave: string) => void
}) {
  const [sopra, setSopra] = useState(false)
  const [nascosta, setNascosta] = useState(() => document.hidden)
  const resta = useRef(DURATA)

  useEffect(() => {
    const cambio = () => setNascosta(document.hidden)
    document.addEventListener('visibilitychange', cambio)
    return () => document.removeEventListener('visibilitychange', cambio)
  }, [])

  // Il conto riparte da dove si era fermato: il tempo passato con il puntatore sopra non si perde.
  useEffect(() => {
    if (sopra || nascosta) return
    const inizio = Date.now()
    const t = window.setTimeout(() => chiudi(voce.chiave), resta.current)
    return () => {
      window.clearTimeout(t)
      resta.current = Math.max(1_500, resta.current - (Date.now() - inizio))
    }
  }, [sopra, nascosta, voce.chiave, chiudi])

  const Icona = voce.genere === 'email' ? IconaBusta : voce.tipo === TIPO_MESSENGER ? IconaMessaggi : IconaCampana
  return (
    <div className={`avvisi-riquadro avvisi-${voce.genere}`}
         onMouseEnter={() => setSopra(true)} onMouseLeave={() => setSopra(false)}
         onFocus={() => setSopra(true)} onBlur={() => setSopra(false)}>
      <button type="button" className="avvisi-apri" onClick={() => apri(voce)}>
        <Icona className="avvisi-icona" />
        <span className="avvisi-testi">
          <span className="avvisi-intestazione">{voce.intestazione}</span>
          <span className="avvisi-titolo">{voce.titolo}</span>
          {voce.testo && <span className="avvisi-testo">{voce.testo}</span>}
          {voce.allarme && (
            <span className="avvisi-allarme" title={voce.allarme.titolo}>
              <Etichetta tono={voce.allarme.tono}>{voce.allarme.testo}</Etichetta>
            </span>
          )}
        </span>
      </button>
      <BottoneIcona icona={<IconaChiudi />} etichetta="Chiudi l’avviso" variante="discreto"
                    onClick={() => chiudi(voce.chiave)} />
    </div>
  )
}
